import {
  ICanvasElementDrawArgs,
  ICanvasTextOptions,
  ACanvasTextElement
} from '@/models/marketingCenter/canvas';
import { printAtWithBreakStrategy } from '@/utils/canvas';

class TextCanvasElement extends ACanvasTextElement {
  constructor(private readonly options: ICanvasTextOptions) {
    super();
  }

  defaultValues = {
    family: 'Inter',
    size: 7,
    lineHeight: 9,
    weight: '400',
    color: '#FFF',
    maxWidth: 108,
    align: 'left' as CanvasTextAlign,
    numberOfLines: undefined
  };

  render(
    container: HTMLDivElement,
    context: CanvasRenderingContext2D,
    args: ICanvasElementDrawArgs
  ) {
    const { defaultValues, options } = this;
    const { font = defaultValues, position, text: textOptions } = options;

    let text = args.text;

    if (textOptions) {
      text = typeof textOptions === 'string' ? textOptions : textOptions(args);
    }

    const {
      family = defaultValues.family,
      size = defaultValues.size,
      lineHeight = defaultValues.lineHeight,
      weight = defaultValues.weight,
      color = defaultValues.color,
      maxWidth = defaultValues.maxWidth,
      align = defaultValues.align,
      numberOfLines: maxNumberOfLines
    } = font;

    context.font = `${weight} ${size}px "${family}"`;

    const { x, y } = this.transformPosition(
      context,
      args,
      text,
      position.x,
      position.y,
      lineHeight,
      maxWidth
    );

    this.renderNextYCoordAvailble = printAtWithBreakStrategy(
      (eachText, x, y, fontSize) => {
        const el = document.createElement('p');
        el.textContent = eachText;

        el.style.position = 'absolute';
        el.style.fontFamily = family;
        el.style.fontSize = `${fontSize || size + 0.5}px`;
        el.style.width = `${maxWidth}px`;
        el.style.letterSpacing = `-0.3px`;
        el.style.fontWeight = `${weight}`;
        el.style.color = `${color}`;
        el.style.textAlign = align;
        el.style.top = `${y}px`;
        el.style.left = `${x}px`;

        container.appendChild(el);
      },
      context,
      text,
      x,
      y + this.renderOffsetTop,
      lineHeight,
      maxWidth,
      maxNumberOfLines
    );
  }

  async draw(context: CanvasRenderingContext2D, args: ICanvasElementDrawArgs) {
    const { defaultValues, options } = this;
    const { font = defaultValues, position, text: textOptions } = options;

    let text = args.text;

    if (textOptions) {
      text = typeof textOptions === 'string' ? textOptions : textOptions(args);
    }

    if (context && text) {
      const {
        family = defaultValues.family,
        size = defaultValues.size,
        lineHeight = defaultValues.lineHeight,
        weight = defaultValues.weight,
        color = defaultValues.color,
        maxWidth = defaultValues.maxWidth,
        align = defaultValues.align,
        numberOfLines
      } = font;

      context.textAlign = align as CanvasTextAlign;
      context.font = `${weight} ${size}px "${family}"`;
      context.fillStyle = color;

      const textWidth = this.getTextWidth(context, text, maxWidth);
      const { x, y } = this.transformPosition(
        context,
        args,
        text,
        position.x,
        position.y,
        lineHeight,
        maxWidth
      );

      let posY = y + (size - 1.4) + this.offsetTop;
      let posX = x;

      if (align === 'center') {
        if (textWidth < maxWidth) {
          posX += maxWidth / 2;
        } else {
          posX += textWidth / 2;
        }
      }

      this.nextYCoordAvailble = printAtWithBreakStrategy(
        (text, x, y) => context.fillText(text, x, y),
        context,
        text,
        posX,
        posY,
        lineHeight,
        maxWidth,
        numberOfLines
      );
    }
  }

  transformPosition(
    context: CanvasRenderingContext2D,
    args: ICanvasElementDrawArgs,
    text: string,
    x: number,
    y: number,
    lineHeight: number,
    maxWidth: number
  ) {
    const ctxTextWidth = context.measureText(text).width;
    const textWidth = this.getTextWidth(context, text, maxWidth);

    const numberOfLines =
      ctxTextWidth > maxWidth ? ctxTextWidth / maxWidth : ctxTextWidth;
    const textHeight = lineHeight * numberOfLines;

    const posX = x < 0 ? args.canvas.width - textWidth + x : x;
    const posY = y < 0 ? args.canvas.height - textHeight + y : y;

    return {
      x: posX,
      y: posY
    };
  }

  getTextWidth(
    context: CanvasRenderingContext2D,
    text: string,
    maxWidth: number
  ) {
    const ctxTextWidth = context.measureText(text).width;
    return ctxTextWidth > maxWidth ? maxWidth : ctxTextWidth;
  }
}

export default TextCanvasElement;
